import { useCallback, useState } from 'react'
import { MeetingPreparationError } from '@models/meeting-errors'
import { apolloClient } from '@lib/apollo/client'
import { GET_APPOINTMENT } from '@graphql/meeting'
import type { GetAppointmentQuery, GetAppointmentQueryVariables } from '@models/generated/graphql'
import type { EnsureMeetingParams } from '@models/meeting-hooks'

export function usePrepareMeeting() {
  const [error, setError] = useState<Error | null>(null)
  const [isPreparing, setIsPreparing] = useState(false)

  const ensureMeeting = useCallback(
    async (params: EnsureMeetingParams) => {
      const normalizedAppointmentId = params.appointmentId?.trim() || null

      if (!normalizedAppointmentId) {
        throw new MeetingPreparationError('Aucun identifiant de réunion fourni.')
      }

      setIsPreparing(true)
      setError(null)

      try {
        // Fetch appointment data for preview (no mutation - just query)
        const { data } = await apolloClient.query<GetAppointmentQuery, GetAppointmentQueryVariables>({
          query: GET_APPOINTMENT,
          variables: { id: normalizedAppointmentId },
          fetchPolicy: 'cache-first'
        })

        const appointment = data?.appointment

        if (!appointment) {
          throw new Error('Appointment not found')
        }

        // Return appointment data for preview purposes
        // The actual meeting creation will happen when user clicks "join room"
        return appointment
      } catch (prepareError) {
        const errorInstance =
          prepareError instanceof Error ? prepareError : new Error('Impossible de préparer la réunion.')
        setError(errorInstance)
        throw errorInstance
      } finally {
        setIsPreparing(false)
      }
    },
    []
  )

  return {
    ensureMeeting,
    isPreparing,
    error
  }
}
